import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { authApi } from '../lib/api';
import { Star, Mail, Loader2, CheckCircle, Shield, Zap } from 'lucide-react';

// Admin-Schnelllogin gibt es nur lokal (DEV_MODE im Backend). In Produktion: Login-Link per Mail
// oder per SSH: docker exec immoverita-backend-1 python -m backend.manage admin-link <mail>
const DEV_LOGIN_VISIBLE = process.env.NODE_ENV === 'development';

export default function AdminLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState(null);
  const [quickLoading, setQuickLoading] = useState(false);

  // Auto-fill demo email if demo param is set
  useEffect(() => {
    if (searchParams.get('demo') === 'admin') {
      setEmail('admin@immoverita.de');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      const response = await authApi.requestAdminMagicLink(email);
      setSent(true);
      setDevToken(response.data.dev_token);
      toast.success('Login-Link wurde gesendet!');
    } catch (err) {
      toast.error('Fehler beim Senden des Login-Links');
    } finally {
      setLoading(false);
    }
  };

  // Quick login — uses dev bypass endpoint, no rate limiting
  const handleQuickLogin = async () => {
    setQuickLoading(true);
    try {
      const res = await authApi.devQuickLogin('admin');
      localStorage.setItem('admin_auth_token', res.data.token);
      localStorage.setItem('user_data', JSON.stringify(res.data.user));
      toast.success('Erfolgreich eingeloggt!');
      navigate('/admin');
    } catch (err) {
      toast.error('Quick-Login fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    } finally {
      setQuickLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (devToken) {
      navigator.clipboard.writeText(`${window.location.origin}/admin/auth/verify?token=${devToken}`);
      toast.success('Link kopiert!');
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
            <Star className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Immoverita</span>
        </Link>

        <Card className="card-shadow">
          <CardHeader className="text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="text-xl">Admin Login</CardTitle>
            <CardDescription>
              {sent ? 'Prüfen Sie Ihre E-Mails' : 'Zugang nur für Administratoren'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!sent ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Admin E-Mail-Adresse</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@immoverita.de"
                      className="pl-10"
                      required
                      data-testid="admin-login-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full"
                  data-testid="admin-login-submit-button"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Login-Link senden
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Wir haben einen Login-Link an <strong>{email}</strong> gesendet.
                  </p>
                </div>
                
                {DEV_LOGIN_VISIBLE && devToken && (
                  <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200 space-y-2">
                    <p className="text-xs font-medium text-yellow-800">
                      Testphase: Der Mailversand ist noch nicht aktiv — direkt einloggen.
                    </p>
                    <Button
                      onClick={() => navigate(`/admin/auth/verify?token=${devToken}`)}
                      className="w-full bg-yellow-500 hover:bg-yellow-600 text-yellow-900"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Mit Magic Link einloggen
                    </Button>
                    <button onClick={handleCopyToken} className="text-[11px] text-yellow-800 underline w-full text-center">
                      Link kopieren
                    </button>
                  </div>
                )}
                
                <Button
                  variant="ghost"
                  onClick={() => { setSent(false); setDevToken(null); }}
                  className="w-full"
                >
                  Andere E-Mail verwenden
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Login Button - only in development */}
        {DEV_LOGIN_VISIBLE && (
          <Card className="border-yellow-300 bg-yellow-50 mt-4">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-800">Quick Demo Login</span>
              </div>
              <Button
                onClick={handleQuickLogin}
                disabled={quickLoading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-yellow-900"
                data-testid="admin-quick-login-button"
              >
                {quickLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Zap className="w-4 h-4 mr-2" />
                )}
                Als admin@immoverita.de einloggen
              </Button>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-sm text-muted-foreground mt-6">
          <Link to="/" className="hover:text-foreground">Zurück zur Startseite</Link>
          {' · '}
          <Link to="/app/login" className="hover:text-foreground">HV Login</Link>
        </p>
      </div>
    </div>
  );
}
